import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const KEEPALIVE_INTERVAL_MS = 25000;

const getSocketUrl = () => {
  if (typeof window !== 'undefined') {
    const { hostname, origin, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
      return `${protocol}//${hostname}:3001`;
    }
    return origin;
  }
  return process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
};

type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

// Clave en window para que el singleton sobreviva HMR de webpack
const SOCKET_KEY = '__gato_retro_socket_singleton__';

const getOrCreateSocket = (): Socket => {
  const win = window as unknown as Record<string, unknown>;
  const existing = win[SOCKET_KEY] as Socket | undefined;
  if (existing) {
    return existing;  // reusar siempre: Socket.IO gestiona la reconexión internamente
  }
  const socketUrl = getSocketUrl();
  console.log('🔌 Creando socket hacia', socketUrl);
  const socket = io(socketUrl, {
    transports: ['websocket', 'polling'],
    timeout: 20000,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnection: true,
    reconnectionAttempts: Infinity,
  });
  win[SOCKET_KEY] = socket;
  return socket;
};

export const useSocket = () => {
  const socketRef = useRef<Socket>(getOrCreateSocket());
  const [isConnected, setIsConnected] = useState(socketRef.current.connected);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    socketRef.current.connected ? 'connected' : 'connecting'
  );
  const lastPongAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const socket = socketRef.current;

    // Sync inmediato por si ya estaba conectado
    if (socket.connected) {
      setIsConnected(true);
      setConnectionStatus('connected');
    }

    const onConnect = () => {
      setIsConnected(true);
      setConnectionStatus('connected');
    };
    const onDisconnect = () => {
      setIsConnected(false);
      setConnectionStatus('offline');
    };
    const onConnectError = () => {
      setConnectionStatus('offline');
    };
    const onReconnectAttempt = () => setConnectionStatus('reconnecting');
    const onReconnect = () => {
      setIsConnected(true);
      setConnectionStatus('connected');
    };
    const onReconnectFailed = () => setConnectionStatus('offline');

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect', onReconnect);
    socket.io.on('reconnect_failed', onReconnectFailed);

    // Polling de respaldo: si el socket conectó pero perdimos el evento
    // (ventana de StrictMode cleanup/remount), lo detectamos aquí
    const pollId = setInterval(() => {
      if (socket.connected) {
        setIsConnected(true);
        setConnectionStatus('connected');
      }
    }, 500);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect', onReconnect);
      socket.io.off('reconnect_failed', onReconnectFailed);
      clearInterval(pollId);
      // NO cerramos el socket: es un singleton compartido
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;

    const onPong = () => {
      lastPongAtRef.current = Date.now();
    };

    socket.on('client-pong', onPong);

    const keepAliveId = window.setInterval(() => {
      if (!socket.connected) return;
      socket.emit('client-ping', { t: Date.now() });
    }, KEEPALIVE_INTERVAL_MS);

    const ensureConnected = () => {
      if (document.visibilityState !== 'visible') return;
      if (!socket.connected) {
        socket.connect();
        return;
      }
      const idleForMs = Date.now() - lastPongAtRef.current;
      if (idleForMs > KEEPALIVE_INTERVAL_MS * 3) {
        socket.emit('client-ping', { t: Date.now() });
      }
    };

    document.addEventListener('visibilitychange', ensureConnected);
    window.addEventListener('focus', ensureConnected);

    return () => {
      socket.off('client-pong', onPong);
      window.clearInterval(keepAliveId);
      document.removeEventListener('visibilitychange', ensureConnected);
      window.removeEventListener('focus', ensureConnected);
    };
  }, []);

  return { socket: socketRef.current, isConnected, connectionStatus };
};
